import { observable, action, computed, extendObservable } from 'mobx';

import {
  AppRegistry,
  AppManager,
  QueryRegistry,
  App,
  Variables,
} from 'core/index';

// -- cache prev results for as long as appmanager is active - as google does
//    => don't use single watch query but individual requests
// -- cancel prev results

export class OptionsBaseManager extends AppManager {
  @observable highlighted = null;
  @observable showOptions;
  @observable optionQuery = null;
  keyboardMode = false;

  SetKeyboardModeOn = () => {
    this.keyboardMode = true;
    window.addEventListener('mousemove', this.SetKeyboardModeOff, true);
  };

  SetKeyboardModeOff = () => {
    this.keyboardMode = false;
    window.removeEventListener('mousemove', this.SetKeyboardModeOff, true);
  };

  @action Select = () => {
    if (this.highlighted) {
      this.fieldStore.Select(this.highlighted);
    }
  };

  @computed get loading() {
    return this.optionQuery?.loading || false;
  }

  @action Highlight = app => {
    if (!app?.disabled) {
      this.highlighted = app;
    }
  };

  highlightedIndex() {
    return this.selectables.indexOf(this.highlighted);
  }

  @action highlightNext = () => {
    this.SetKeyboardModeOn();
    const index = this.highlightedIndex();
    if (index === -1) {
      // eslint-disable-next-line
      this.Highlight(this.selectables.slice()[0]);
    } else if (index !== this.selectables.length - 1) {
      this.Highlight(this.selectables.slice()[index + 1]);
    }
    if (this.highlighted?.isHeader) {
      if (index !== this.selectables.length - 1) {
        this.highlightNext();
      } else {
        this.highlightPrev();
      }
    }
  };

  @action highlightPrev = () => {
    this.SetKeyboardModeOn();
    const index = this.highlightedIndex();
    if (index === -1) {
      this.Highlight(this.selectables.slice()[this.selectables.length - 1]);
    } else if (index !== 0) {
      this.Highlight(this.selectables.slice()[index - 1]);
    } else {
      this.highlighted = null;
    }
    if (this.highlighted?.isHeader) {
      if (index !== 0) {
        this.highlightPrev();
      } else {
        this.highlightNext();
      }
    }
  };

  @action highlightFirst = () => {
    if (
      this.selectables &&
      this.selectables.length !== 0 &&
      !this.highlighted &&
      !this.highlightedToExpand
    ) {
      this.Highlight(this.selectables.slice()[0]);
      if (this.highlighted?.isHeader) {
        this.highlightNext();
      }
    }
  };

  @action ToggleShow(val) {
    /* Not used by AutocompleteManager. */
    if (val !== undefined) {
      this.showOptions = val;
    } else if (this.showOptions) {
      this.showOptions = false;
    } else {
      this.showOptions = true;
    }
    if (this.showOptions === true) {
      if (!this.conf.hideSelected) {
        this.HighlightSelected();
      }
    } else {
      this.Highlight(null);
    }
  }

  @action HighlightSelected = () => {
    this.Highlight(this.fieldStore.getSelectedOption);
    /* SetExpandedApp to parent of highlighted */
    if (this.SetExpandedApp) {
      if (this.highlighted) {
        let highlightedCategory = this.highlighted;
        if (highlightedCategory[this.conf.nestedSelectables] === null) {
          // It's a leaf node not a CategoryApp so the parent App is one levelUp.
          highlightedCategory = highlightedCategory[this.conf.parentSelectable];
        }
        // 'Highlighted' is a CategoryApp, and already represents the Category level.
        this.SetExpandedApp(highlightedCategory, true, false);
      }
    }
  };

  optionExists = (key, value) => {
    /* Check if the Option exists. If it doesn't we probably want to remove it from being 'selected'. */
    if (!this.selectables || value === null) {
      return null;
    }
    let optionValue = value;
    if (!!optionValue && optionValue instanceof Object) {
      optionValue = optionValue[key];
    }

    return this.selectables.some(opt => opt[key] === optionValue);
  };
}

export class OptionsManager extends OptionsBaseManager {
  @observable highlightedToExpand = null;

  constructor(parent, init = {}, options) {
    super(parent, init, options);
    this.showOptions = false;
    this.conf = this.fieldStore.conf.options;
    if (this.conf.ArrayApp) {
      this.ArrayApp = AppRegistry.get(this.conf.ArrayApp);
    }
    if (this.conf.ArrayApp && !this.ArrayApp) {
      console.error(`${this.conf.ArrayApp} is not in the AppRegistry`);
    }
    if (this.ArrayApp) {
      this.ElementModel = this.ArrayApp.ElementApp.Model;
      this.AppRef('options', this.ArrayApp);
    } else {
      extendObservable(this, { options: [] });
    }
    this.ResultUpdate(this.getStaticOptions());
    if (this.conf.query) {
      const qVariables = this.conf.queryVariables;
      if (qVariables) {
        const vars = {};
        // eslint-disable-next-line
        for (const key of Object.keys(qVariables)) {
          if (this.conf.queryVariableTypes) {
            if (this.conf.queryVariableTypes[key] === 'json') {
              vars[key] = () => JSON.stringify(qVariables[key]);
              // eslint-disable-next-line
              continue;
            }
          }
          vars[key] = () => qVariables[key];
        }
        this.variables = new Variables(vars);
      }
      this.query = QueryRegistry.get(this.conf.query);
      if (this.conf.query && !this.query) {
        throw Error(`Query '${this.conf.query}' is not in the registry`);
      }

      if (this.conf.requeryOn) {
        this.variables.onChange(this.conf.requeryOn, this.NewQuery);
      } else {
        this.NewQuery();
      }
    }
  }

  dispose() {
    super.dispose();
    if (this.fieldStore.value instanceof App) {
      // in general this should be done when exiting the form but currently
      // not implemented when existing a Modal
      this.fieldStore.value.ResetLocals();
    }
  }

  @action HighlightByPrefix = prefix => {
    const index = this.selectables.findIndex(opt => {
      if (typeof opt[this.fieldStore.conf.labelKey] === 'string') {
        if (
          opt[this.fieldStore.conf.labelKey]
            .toLowerCase()
            .startsWith(prefix.toLowerCase())
        ) {
          return true;
        }
      }
      return false;
    });
    if (index === -1) {
      this.Highlight(null);
    } else {
      this.Highlight(this.selectables[index]);
    }
  };

  @action Select = () => {
    if (this.highlighted) {
      this.fieldStore.Select(this.highlighted);
      if (!this.conf.keepOpenOnSelect) {
        this.ToggleShow(false);
      }
    }
  };

  getStaticOptions() {
    if (this.conf.values) {
      if (this.ArrayApp) {
        return observable(
          // why not this.ElementModel.Load(val)
          this.conf.values.map(val => new this.ElementModel(this, val)),
        );
      }
      return this.conf.values;
    }
    return observable([]);
  }

  @action NewQuery = () => {
    if (!this.query) return;
    // eslint-disable-next-line
    this.optionQuery = new this.query(this, {
      queryKey: this.query.constructor.name,
    });
    this.optionQuery.process(list => {
      if (list === null) {
        return this.getStaticOptions();
      }
      if (this.ElementModel) {
        return list.map(element => this.ElementModel.Load(element));
        /* Using ArrayApp is not currently compatible with staticOptions.
          We would need to either have ArrayApp be initialised with a list of
          local (non-upstream) Apps (see comments in ArrayApp) OR
          to create some "fake" upstream models from the static options.

          return observable(this.getStaticOptions().concat(
            list.map((element) => this.ElementModel.Load(element))
          )); */
      }
      return this.getStaticOptions().concat(list.slice());
    }, this.ResultUpdate);
    this.optionQuery.query();
  };

  @computed get selectables() {
    if (this.conf.hideSelected) {
      return this.options.filter(opt => !this.fieldStore.isSelected(opt));
    }
    return this.options;
  }

  @action ResultUpdate = arrayOfModels => {
    this.options = arrayOfModels;
    if (this.options === null) {
      this.Highlight(null);
    } else {
      this.HighlightSelected();
    }
    /*  Remove selection where the options no-longer exist. Problematic because:
    a) it causes extra queries potentially in loops
    b) the TreeOptionsManger selectExists computed doesn't exist */
    if (
      !this.optionExists(
        this.fieldStore.conf.appKey,
        this.fieldStore.getSelectedOption,
      )
    ) {
      // this.fieldStore.ResetValue();
    }
  };
}

export class TreeOptionsManager extends OptionsManager {
  /* Used for materials and process tree selection fields.
  For now only tested with ReferenceField.
  Requires the selected option to implement getPath returning objects */
  @observable expandedApp = null;

  @action Highlight = app => {
    if (app === null) {
      this.highlighted = null;
    } else if (
      !app.isExpandable ||
      (app.categoryAppMode && app.isSelectableCategory)
    ) {
      this.highlighted = app;
      this.highlightedToExpand = null;
    } else {
      this.highlighted = null;
      this.highlightedToExpand = app;
    }
  };

  IsSingleExpandable = app => {
    /* Is this an expandable app and the only 'selectable' at it's level?
    If so we'll expand it automatically */
    if (!app?.isExpandable) {
      return false;
    }
    const parentSelectable = app[this.conf.parentSelectable];
    if (parentSelectable) {
      if (parentSelectable[this.conf.nestedSelectables].length === 1) {
        return true;
      }
      return false;
    }
    // this is a top level item
    if (this.selectables.length === 1) {
      return true;
    }
    return false;
  };

  @computed get expandedPath() {
    const selectedPath = [];
    let expandPath = [];
    /* if (this.fieldStore.getSelectedOption) {
      // the path () of the selected element
      selectedPath = this.fieldStore.getSelectedOption.getPath.slice(0, -1).map((app) => app.id);
    } */
    if (this.expandedApp) {
      // also show the last explicitly expanded element
      expandPath = this.expandedApp.getPath.map(app => app.id);
    }
    selectedPath.push(...expandPath);
    return selectedPath;
  }

  @action SetExpandedApp = (app, open = false, highlightNext = true) => {
    if (app) {
      if (this.expandedApp?.getPath.includes(app) && open === false) {
        // if the clicked expanded app is already selected - select the level above it (close it).
        this.expandedApp = app[this.conf.parentSelectable];
      } else {
        this.expandedApp = app;
        if (highlightNext) this.highlightNext();
      }
    }
  };

  getPath(app) {
    /* Return the hierarchy for any app as an array of apps representing
    the various levels of the hierarchy */
    if (!app) {
      return [];
    }
    return app.getPath;
  }

  getDirFromPath(path) {
    if ([0, 1].includes(path.length)) {
      return null;
    }
    return path[path.length - 2];
  }

  appSelectablesIx = (selectables, app, appKey) => {
    return selectables.findIndex(
      selectable => app[appKey] === selectable[appKey],
    );
  };

  optionExists = (appKey, val) => {
    /* val should always be an App which implements 'getPath' */
    if (!this.selectables || val === null) {
      return null;
    }
    if (this.getAppFromSelectables(this.selectables, val.getPath, appKey)) {
      return true;
    }
    return false;
  };

  getAppFromSelectables = (selectables, appPath, appKey) => {
    /* Confirms that the app exists in the current selectables or returns null */
    const ix = this.appSelectablesIx(selectables, appPath[0], appKey);
    if (ix === -1) {
      return null;
    }
    if (appPath.length === 1) {
      return selectables.index(ix);
    }
    return this.getAppFromSelectables(
      selectables.index(ix)[this.conf.nestedSelectables],
      appPath.slice(1),
      appKey,
    );
  };

  selectablesForHighlighted = highlighted => {
    const highlightedPath = this.getPath(highlighted);
    const dir = this.getDirFromPath(highlightedPath);
    let selectables;
    if (dir === null) {
      // this is a top-level element;
      // eslint-disable-next-line prefer-destructuring
      selectables = this.selectables;
    } else {
      selectables = dir[this.conf.nestedSelectables];
    }
    return selectables;
  };

  isExpanded = app => {
    return this.expandedApp?.getPath.includes(app);
  };

  firstHighlightableParent(highlighted) {
    if (!this.IsSingleExpandable(highlighted[this.conf.parentSelectable])) {
      return highlighted[this.conf.parentSelectable];
    }
    return this.firstHighlightableParent(
      highlighted[this.conf.parentSelectable],
    );
  }

  increment = highlighted => {
    if (!highlighted) {
      return null;
    }
    const selectables = this.selectablesForHighlighted(highlighted);
    const ix = this.appSelectablesIx(
      selectables,
      highlighted,
      this.fieldStore.conf.labelKey,
    );
    if (
      ix === selectables.length - 1 &&
      highlighted[this.conf.parentSelectable] &&
      !highlighted.categoryAppMode
    ) {
      if (
        !highlighted.isExpandable ||
        !(highlighted.isExpandable && this.isExpanded(highlighted))
      ) {
        // increment from the last element in one category to the next element one level up
        // e.g. from Metal→ Alumnimim → X TO Metal -> Brass

        // the below condition will stop us from incrementing off the end of a singleExpandable
        // Could be replaced with something recursive that looks for a parentSelectable further up the tree
        const highlightableParent = this.firstHighlightableParent(highlighted);
        if (highlightableParent) {
          this.SetExpandedApp(highlightableParent, false, false); // close the expanded App
          return this.increment(highlightableParent); // highlight the next on the next level up;
        }
      }
    }

    let next = null;
    if (
      highlighted.isExpandable &&
      this.expandedApp?.getPath.includes(highlighted)
    ) {
      // We are descending into an expanded Category
      // If it is selectable select the category "Any ${app.name}"
      if (highlighted.isSelectableCategory && !highlighted.categoryAppMode) {
        // eslint-disable-next-line no-param-reassign
        highlighted.categoryAppMode = true;
        next = highlighted;
      } else {
        // eslint-disable-next-line no-param-reassign
        highlighted.categoryAppMode = false;
        next = highlighted[this.conf.nestedSelectables].index(0);
      }
    } else {
      next = selectables.index(Math.min(ix + 1, selectables.length - 1));
    }
    // skip over single expandables which are always 'expanded'
    if (
      this.IsSingleExpandable(next) &&
      next.isSelectableCategory &&
      !next.categoryAppMode
    ) {
      next.categoryAppMode = true;
      return next;
    }
    // deal with isSingleExpandable when !isSelectableCategory
    if (this.IsSingleExpandable(next) && !next.isSelectableCategory) {
      return this.increment(next);
    }
    return next;
  };

  decrement = highlighted => {
    const selectables = this.selectablesForHighlighted(highlighted);
    const ix = this.appSelectablesIx(
      selectables,
      highlighted,
      this.fieldStore.conf.labelKey,
    );
    let prev = null;
    const origCategoryAppMode = highlighted.categoryAppMode;
    if (highlighted.isSelectableCategory && highlighted.categoryAppMode) {
      // eslint-disable-next-line no-param-reassign
      highlighted.categoryAppMode = false;
      prev = highlighted;
    } else {
      prev = highlighted[this.conf.parentSelectable];
      if (ix - 1 < 0 && prev) {
        if (prev.isSelectableCategory) {
          prev.categoryAppMode = true;
        }
      } else {
        const newIx = Math.max(ix - 1, 0);
        prev = selectables.index(newIx);
      }
    }
    // ensure no infinite cycles
    if (
      prev?.id === highlighted.id &&
      prev?.categoryAppMode === origCategoryAppMode
    ) {
      return prev;
    }
    // skip over single expandables which are always 'expanded'
    if (this.IsSingleExpandable(prev)) {
      if (!prev.isSelectableCategory) {
        return this.decrement(prev);
      }
      if (!prev.categoryAppMode) {
        return this.decrement(prev);
      }
    }
    return prev;
  };

  highlightAdj = (incr = this.increment) => {
    /* take into account CategoryApps */
    this.SetKeyboardModeOn();
    let { highlighted } = this;
    if (!this.highlighted && this.highlightedToExpand) {
      highlighted = this.highlightedToExpand;
    }
    if (!highlighted) {
      // Nothing is highlighted, highlightFirst?
      return;
    }
    const next = incr(highlighted);
    if (!next) {
      this.Highlight(null);
    } else {
      this.Highlight(next);
    }
  };

  @action highlightNext = () => {
    this.highlightAdj(this.increment);
  };

  @action highlightPrev = () => {
    this.highlightAdj(this.decrement);
  };
}
